import { BaseResource } from './base';
import {
  StartOnboardingData,
  StartOnboardingResponse,
  UpdateGymSettingsData,
  ConfigureFeaturesData,
  CompleteGuidedSetupData,
  OnboardingStatus,
  OnboardingResponse,
  OnboardingStatusResponse,
  OnboardingCacheStatus,
} from '../models/onboarding';

export class OnboardingResource extends BaseResource {
  /**
   * Start onboarding process (async)
   * Creates user account and triggers background setup
   * Returns immediately with PROCESSING status
   * Use getStatusByEmail() to poll for completion
   */
  async start(data: StartOnboardingData): Promise<StartOnboardingResponse> {
    const response = await this.client.post<StartOnboardingResponse>(
      '/onboarding/start',
      data,
      { skipAuth: true }, // Public endpoint - no auth required
    );

    // Store tokens after successful onboarding start
    if (response.access_token) {
      this.client.setAuthToken(response.access_token);
    }

    return response;
  }

  /**
   * Get onboarding status by email (polling endpoint)
   * Use this to check if async onboarding setup is complete
   *
   * @param email - User email to check status
   * @returns Status object with PROCESSING, COMPLETED, or FAILED
   */
  async getStatusByEmail(email: string): Promise<OnboardingStatusResponse> {
    return this.client.get<OnboardingStatusResponse>(
      '/onboarding/status',
      { email },
      { skipAuth: true },
    );
  }

  /**
   * Poll for onboarding completion
   * Automatically polls until status is COMPLETED or FAILED
   *
   * @param email - User email
   * @param options - Polling options
   * @returns Final status when completed or failed
   */
  async waitForCompletion(
    email: string,
    options: {
      maxAttempts?: number;
      intervalMs?: number;
      onProgress?: (attempt: number, status: OnboardingCacheStatus) => void;
    } = {},
  ): Promise<OnboardingStatusResponse> {
    const { maxAttempts = 60, intervalMs = 1000, onProgress } = options;

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      const status = await this.getStatusByEmail(email);

      if (onProgress) {
        onProgress(attempt, status.status);
      }

      if (status.status === OnboardingCacheStatus.COMPLETED) {
        return status;
      }

      if (status.status === OnboardingCacheStatus.FAILED) {
        throw new Error(`Onboarding failed: ${status.error || 'Unknown error'}`);
      }

      // Wait before next poll
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }

    throw new Error('Onboarding timeout - process is still running in background');
  }

  /**
   * Updates basic gym settings (step 2 of onboarding)
   * Configure basic gym information like name, address, etc.
   */
  async updateGymSettings(data: UpdateGymSettingsData): Promise<OnboardingResponse> {
    return this.client.put<OnboardingResponse>('/onboarding/gym-settings', data);
  }

  /**
   * Configure gym features (step 3 of onboarding)
   * Enable/disable various management features
   */
  async configureFeatures(data: ConfigureFeaturesData): Promise<OnboardingResponse> {
    return this.client.put<OnboardingResponse>('/onboarding/configure-features', data);
  }

  /**
   * Mark onboarding as complete
   */
  async completeSetup(data: CompleteGuidedSetupData): Promise<OnboardingResponse> {
    return this.client.post<OnboardingResponse>('/onboarding/complete', data);
  }

  /**
   * Get current onboarding status by gym ID
   * Check progress and next steps
   */
  async getStatus(gymId: string): Promise<OnboardingStatus> {
    return this.client.get<OnboardingStatus>(`/onboarding/status/${gymId}`);
  }
}
